import { useEffect, useMemo, useRef, useState } from 'react';
import type { MessageRow } from '../lib/api';
import { prettyAgentMono, prettyAgentTitle } from '../lib/agentName';
import { Prism } from './Prism';
import { AuthorizeCard } from './AuthorizeCard';
import { DispatchStrip, type DispatchGroup } from './DispatchIndicator';
import { InteractivePromptCard } from './InteractivePromptCard';

interface Props {
  messages: MessageRow[];
  pending: boolean;
  sessionId?: string;
  agentSlug: string;
  onAuthorizeDecided?: () => void | Promise<void>;
  /**
   * Phase: empty-state spawn affordance. The agent does not auto-introduce
   * on session creation; the human clicks Spawn to fire a boot turn. Mark
   * validated this UX 2026-05-07: an explicit Spawn button replaced the
   * misleading "Say something to begin" placeholder, since the agent's
   * first turn is always a full boot regardless of the user's text.
   */
  onSpawn: () => void | Promise<void>;
}

type RenderItem =
  | { kind: 'user'; id: string; message: MessageRow }
  | { kind: 'agent'; id: string; message: MessageRow }
  | { kind: 'dispatch'; id: string; group: DispatchGroup }
  | { kind: 'authorize'; id: string; proposal: MessageRow; decision?: MessageRow }
  | { kind: 'interactive'; id: string; prompt: MessageRow };

function groupMessages(messages: MessageRow[]): RenderItem[] {
  const items: RenderItem[] = [];
  const dispatches = new Map<string, DispatchGroup>();
  const dispatchSeenIds = new Set<string>();
  // proposal_id -> index into items, so we can attach decisions later
  const proposalIndex = new Map<string, number>();

  for (const m of messages) {
    if (m.kind === 'user_message') {
      items.push({ kind: 'user', id: m.id, message: m });
      continue;
    }
    if (m.kind === 'agent_message') {
      items.push({ kind: 'agent', id: m.id, message: m });
      continue;
    }
    if (m.kind === 'authorize_proposal') {
      const idx = items.length;
      items.push({ kind: 'authorize', id: m.id, proposal: m });
      proposalIndex.set(m.id, idx);
      continue;
    }
    if (m.kind === 'authorize_decision') {
      const meta = safeMeta(m.metadata);
      const proposal_id = (meta.proposal_id as string | undefined) ?? null;
      if (proposal_id && proposalIndex.has(proposal_id)) {
        const idx = proposalIndex.get(proposal_id)!;
        const item = items[idx];
        if (item && item.kind === 'authorize') {
          items[idx] = { ...item, decision: m };
        }
      }
      continue;
    }
    if (m.kind === 'interactive_prompt') {
      // AskUserQuestion that the spawned claude wanted to ask. The runtime
      // auto-denies it in --print mode; the card is informational only.
      items.push({ kind: 'interactive', id: m.id, prompt: m });
      continue;
    }
    if (
      m.kind !== 'dispatch_started' &&
      m.kind !== 'dispatch_output' &&
      m.kind !== 'dispatch_finished'
    )
      continue;
    if (!m.dispatch_id) continue;

    let g = dispatches.get(m.dispatch_id);
    if (!g) {
      g = {
        dispatch_id: m.dispatch_id,
        state: 'started',
        total_bytes: 0,
        first_seen: m.created_at,
      };
      dispatches.set(m.dispatch_id, g);
    }
    if (!dispatchSeenIds.has(m.dispatch_id)) {
      items.push({ kind: 'dispatch', id: 'dispatch-' + m.dispatch_id, group: g });
      dispatchSeenIds.add(m.dispatch_id);
    }

    if (m.kind === 'dispatch_output') {
      g.state = 'running';
      g.total_bytes += m.content?.length ?? 0;
    } else if (m.kind === 'dispatch_finished') {
      g.state = 'finished';
      if (m.metadata) {
        try {
          g.final_meta = JSON.parse(m.metadata) as Record<string, unknown>;
        } catch {
          // ignore
        }
      }
    }
  }
  return items;
}

function safeMeta(metadata: string | null): Record<string, unknown> {
  if (!metadata) return {};
  try {
    return JSON.parse(metadata) as Record<string, unknown>;
  } catch {
    return {};
  }
}

const NEAR_BOTTOM_THRESHOLD_PX = 80;
const VISIBLE_ITEM_CAP = 200;

export function ConversationPane({ messages, pending, sessionId, agentSlug, onAuthorizeDecided, onSpawn }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewBelow, setHasNewBelow] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Reset the "show earlier" expansion when the session id changes; otherwise
  // an expanded long session A leaks its expanded state into freshly loaded
  // session B.
  useEffect(() => {
    setShowAll(false);
  }, [sessionId]);

  const allItems = useMemo(() => groupMessages(messages), [messages]);
  const items = useMemo(
    () => (showAll || allItems.length <= VISIBLE_ITEM_CAP ? allItems : allItems.slice(-VISIBLE_ITEM_CAP)),
    [allItems, showAll],
  );
  const hiddenCount = allItems.length - items.length;
  const itemCountRef = useRef(items.length);

  function scrollToBottom(smooth = true) {
    endRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
    setHasNewBelow(false);
  }

  // Track whether the user is near the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function check() {
      const dist = (el!.scrollHeight - el!.scrollTop) - el!.clientHeight;
      setIsNearBottom(dist <= NEAR_BOTTOM_THRESHOLD_PX);
    }
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, []);

  // On new content: if near bottom, follow it. If scrolled up, show the pill.
  useEffect(() => {
    const grew = items.length > itemCountRef.current;
    itemCountRef.current = items.length;
    if (!grew && !pending) return;
    if (isNearBottom) {
      requestAnimationFrame(() => scrollToBottom(true));
    } else if (grew) {
      setHasNewBelow(true);
    }
  }, [items.length, pending, isNearBottom]);

  return (
    <div className="flex-1 relative min-h-0">
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-y-auto px-4 py-4 flex flex-col gap-2 md:gap-1.5"
      >
        {items.length === 0 && !pending && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <Prism slug={agentSlug} size={48} active />
            <button
              type="button"
              onClick={() => onSpawn()}
              className="simpliloquy-button-focus px-6 py-3 rounded-md
                         font-semibold text-base text-paper-100
                         border transition-colors"
              style={{
                backgroundColor: 'var(--agent-bubble)',
                borderColor: 'var(--agent-edge)',
              }}
            >
              Spawn {prettyAgentTitle(agentSlug)}
            </button>
            <span className="font-mono text-[10px] uppercase tracking-mono-wide text-paper-500">
              boot the agent against its constitution
            </span>
          </div>
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="simpliloquy-button-focus self-center mb-1 px-3 py-2 rounded-md
                       font-mono text-[10px] uppercase tracking-mono-wide
                       text-paper-400 hover:text-paper-200 hover:bg-night-800"
          >
            show {hiddenCount} earlier message{hiddenCount === 1 ? '' : 's'}
          </button>
        )}
        {items.map((item) => {
          if (item.kind === 'user') return <UserBubble key={item.id} message={item.message} />;
          if (item.kind === 'agent') return <AgentBubble key={item.id} message={item.message} />;
          if (item.kind === 'authorize') {
            return (
              <AuthorizeCard
                key={item.id}
                proposal={item.proposal}
                decision={item.decision}
                sessionId={sessionId ?? ''}
                onDecided={() => onAuthorizeDecided?.()}
              />
            );
          }
          if (item.kind === 'interactive') {
            return <InteractivePromptCard key={item.id} prompt={item.prompt} />;
          }
          return <DispatchStrip key={item.id} group={item.group} />;
        })}
        {pending && <ThinkingBubble />}
        <div ref={endRef} />
      </div>

      {hasNewBelow && !isNearBottom && (
        <NewBelowPill onTap={() => scrollToBottom(true)} />
      )}
    </div>
  );
}

function NewBelowPill({ onTap }: { onTap: () => void }) {
  return (
    <button
      type="button"
      onClick={onTap}
      aria-label="Scroll to latest message"
      className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10
                 h-11 min-w-[44px] px-4 rounded-full
                 bg-night-800 border border-night-600
                 hover:bg-night-700 focus:outline-none focus:bg-night-700
                 transition-colors flex items-center gap-2"
    >
      <span
        className="font-mono text-[10px] uppercase tracking-mono-wide"
        style={{ color: 'var(--agent-fg-soft)' }}
      >
        new
      </span>
      <DownChevron />
    </button>
  );
}

function DownChevron() {
  return (
    <svg width={12} height={12} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 6 L8 11 L13 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-paper-300"
      />
    </svg>
  );
}

function UserBubble({ message }: { message: MessageRow }) {
  return (
    <div className="flex justify-end">
      <div
        className="max-w-[75%] md:max-w-[600px] lg:max-w-[640px]
                   rounded-lg px-3.5 py-3 md:py-2 leading-relaxed
                   text-[15px] md:text-[14px] font-semibold whitespace-pre-wrap
                   border"
        style={{
          background: 'var(--agent-user-soft)',
          borderColor: 'var(--agent-user-edge)',
          color: 'var(--agent-user-fg)',
        }}
      >
        {message.content}
      </div>
    </div>
  );
}

function AgentBubble({ message }: { message: MessageRow }) {
  return (
    <div className="flex justify-start">
      <div
        className="max-w-[75%] md:max-w-[600px] lg:max-w-[640px]
                   rounded-lg pl-4 pr-3.5 py-3 md:py-2 leading-relaxed
                   text-[15px] md:text-[14px] whitespace-pre-wrap
                   border-l-4"
        style={{
          background: 'var(--agent-bubble)',
          borderLeftColor: 'var(--agent-edge)',
          color: 'var(--agent-fg-body)',
        }}
      >
        {message.agent_slug && (
          <div
            className="font-mono text-[10px] uppercase tracking-mono-wide mb-1.5"
            style={{ color: 'var(--agent-subtle)' }}
          >
            {prettyAgentMono(message.agent_slug)}
          </div>
        )}
        {message.content}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start">
      <div
        className="max-w-[75%] md:max-w-[600px] simpliloquy-thinking-glow
                   rounded-lg pl-4 pr-3.5 py-3 md:py-2 leading-relaxed
                   text-[15px] md:text-[14px] border-l-4"
        style={{
          background: 'var(--agent-bubble)',
          borderLeftColor: 'var(--agent-edge)',
          color: 'var(--agent-fg-body)',
        }}
      >
        <span
          className="simpliloquy-shimmer font-mono text-[10px] uppercase tracking-mono-wide"
          style={{ color: 'var(--agent-subtle)' }}
        >
          Thinking.
        </span>
      </div>
    </div>
  );
}

